import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Eyebrow, GoldButton, OutlineButton } from "../components/motion";
import { toast } from "sonner";
import { Star, Trash2, Send } from "lucide-react";

const TABS = ["bookings", "favorites", "giftcards", "dietary", "addresses"];

export default function Account() {
  const { t, lang } = useLang();
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("bookings");

  if (ready && !user) {
    return (
      <div className="pt-[72px] min-h-[70vh] flex items-center justify-center">
        <Link to={`/${lang}/login`}><GoldButton data-testid="account-login-btn">{t("nav.login")}</GoldButton></Link>
      </div>
    );
  }

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("account.title")} — Chef à Domicile`} description="" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-16 md:py-20">
        <Eyebrow>{user?.name}</Eyebrow>
        <h1 className="font-serif text-4xl sm:text-5xl text-ivory mb-10">{t("account.title")}</h1>
        <div className="flex flex-wrap gap-2 mb-12 border-b border-white/10 pb-4">
          {TABS.map((k) => (
            <button key={k} data-testid={`account-tab-${k}`} onClick={() => setTab(k)}
              className={`text-xs uppercase tracking-widest px-4 py-2.5 border transition-colors ${tab === k ? "border-gold text-gold bg-gold/5" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>
              {t(`account.${k}`)}
            </button>
          ))}
        </div>
        {tab === "bookings" && <Bookings />}
        {tab === "favorites" && <Favorites />}
        {tab === "giftcards" && <GiftOrders />}
        {tab === "dietary" && <Dietary />}
        {tab === "addresses" && <Addresses />}
      </div>
    </div>
  );
}

function Bookings() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const { data: bookings, isLoading } = useQuery({ queryKey: ["my-bookings"], queryFn: () => api.get("/bookings/my").then((r) => r.data) });
  const cancel = useMutation({
    mutationFn: (id) => api.post(`/bookings/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-bookings"] }),
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading) return <p className="text-neutral-500">{t("common.loading")}</p>;
  if (!bookings?.length) return <p className="text-neutral-400 py-12 text-center border border-white/8" data-testid="bookings-empty">{t("account.noBookings")}</p>;

  return (
    <div className="space-y-5" data-testid="bookings-list">
      {bookings.map((b) => (
        <div key={b.id} className="bg-coal border border-white/8" data-testid={`booking-item-${b.id}`}>
          <div className="flex flex-wrap items-center gap-5 p-6">
            {b.dish_image && <img src={b.dish_image} alt={b.dish_name} className="w-20 h-20 object-cover" />}
            <div className="flex-1 min-w-[180px]">
              <p className="font-serif text-lg text-ivory">{b.dish_name}</p>
              <p className="text-xs text-neutral-500 mt-1">{b.chef_name || "—"} · {b.date} {b.time} · {b.guests} {t("common.guests")}</p>
            </div>
            <div className="text-end">
              <p className="font-serif text-xl text-gold">€{b.total}</p>
              <span className="text-[10px] uppercase tracking-widest text-neutral-400 border border-white/15 px-2 py-0.5 inline-block mt-1" data-testid={`booking-status-${b.id}`}>{b.status}</span>
            </div>
            <div className="flex gap-2">
              <button data-testid={`booking-messages-btn-${b.id}`} onClick={() => setOpenId(openId === b.id ? null : b.id)}
                className="text-xs text-neutral-300 border border-white/15 px-3 py-2 hover:border-gold hover:text-gold transition-colors">
                {t("account.messages")}
              </button>
              {b.status === "completed" && !b.review && (
                <button data-testid={`booking-review-btn-${b.id}`} onClick={() => setReviewFor(reviewFor === b.id ? null : b.id)}
                  className="text-xs text-gold border border-gold/40 px-3 py-2">{t("account.review")}</button>
              )}
              {["draft", "pending", "confirmed", "chef_selected"].includes(b.status) && (
                <button data-testid={`booking-cancel-btn-${b.id}`} onClick={() => cancel.mutate(b.id)}
                  className="text-xs text-neutral-500 border border-white/10 px-3 py-2 hover:text-red-400 hover:border-red-400/40 transition-colors">
                  {t("account.cancelBooking")}
                </button>
              )}
            </div>
          </div>
          {openId === b.id && <Messages id={b.id} />}
          {reviewFor === b.id && <ReviewForm id={b.id} onDone={() => { setReviewFor(null); qc.invalidateQueries({ queryKey: ["my-bookings"] }); }} />}
        </div>
      ))}
    </div>
  );
}

function Messages({ id }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [msg, setMsg] = useState("");
  const { data: messages } = useQuery({ queryKey: ["messages", id], queryFn: () => api.get(`/bookings/${id}/messages`).then((r) => r.data), refetchInterval: 15000 });
  const send = useMutation({
    mutationFn: () => api.post(`/bookings/${id}/messages`, { text: msg }),
    onSuccess: () => { setMsg(""); qc.invalidateQueries({ queryKey: ["messages", id] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="border-t border-white/8 p-6" data-testid={`messages-panel-${id}`}>
      <div className="space-y-3 max-h-56 overflow-y-auto mb-4">
        {(!messages || messages.length === 0) && <p className="text-xs text-neutral-600">{t("common.empty")}</p>}
        {messages?.map((m, i) => (
          <div key={i} className={`text-sm p-3 max-w-[80%] ${m.sender_role === "CUSTOMER" ? "bg-gold/10 border border-gold/20 ms-auto" : "bg-white/5 border border-white/10"}`}>
            <p className="text-ivory">{m.text}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid={`message-input-${id}`} className="lux-input flex-1" placeholder={t("account.writeMsg")} value={msg} onChange={(e) => setMsg(e.target.value)} />
        <button data-testid={`message-send-${id}`} onClick={() => msg && send.mutate()} className="bg-gold text-black px-4 hover:bg-goldlight transition-colors"><Send size={15} /></button>
      </div>
    </div>
  );
}

function ReviewForm({ id, onDone }) {
  const { t } = useLang();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post(`/bookings/${id}/review`, { food_quality: rating, professionalism: rating, punctuality: rating, presentation: rating, overall: rating, comment });
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="border-t border-white/8 p-6" data-testid={`review-form-${id}`}>
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}>
            <Star size={20} className={n <= rating ? "text-gold fill-gold" : "text-neutral-600"} />
          </button>
        ))}
      </div>
      <textarea data-testid="review-comment-input" className="lux-input mb-4" rows={3} placeholder={t("account.reviewComment")} value={comment} onChange={(e) => setComment(e.target.value)} />
      <GoldButton onClick={submit} data-testid="review-submit-btn">{t("common.send")}</GoldButton>
    </div>
  );
}

function Favorites() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { data: favs, isLoading } = useQuery({ queryKey: ["favorites"], queryFn: () => api.get("/favorites").then((r) => r.data) });
  const chefs = favs?.chefs || [];
  if (isLoading) return <p className="text-neutral-500">{t("common.loading")}</p>;
  if (!chefs.length) return <p className="text-neutral-400 py-12 text-center border border-white/8" data-testid="favorites-empty">{t("account.noFavorites")}</p>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-list">
      {chefs.map((c) => {
        return (
          <button key={c.id} onClick={() => c.slug && navigate(`/${lang}/chef/${c.slug}`)} data-testid={`favorite-chef-${c.id}`}
            className="text-start bg-coal border border-white/8 hover:border-gold/40 transition-colors p-5 flex gap-4 items-center">
            {c.photo && <img src={c.photo} alt={c.name} className="w-16 h-16 object-cover" />}
            <div>
              <p className="font-serif text-lg text-ivory">{c.name}</p>
              <p className="text-xs text-neutral-500">{c.city}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function GiftOrders() {
  const { t } = useLang();
  const { data: orders, isLoading } = useQuery({ queryKey: ["giftorders"], queryFn: () => api.get("/giftcards/orders").then((r) => r.data) });
  if (isLoading) return <p className="text-neutral-500">{t("common.loading")}</p>;
  if (!orders?.length) return <p className="text-neutral-400 py-12 text-center border border-white/8" data-testid="giftorders-empty">{t("account.noGiftcards")}</p>;
  return (
    <div className="space-y-4" data-testid="giftorders-list">
      {orders.map((o) => (
        <div key={o.id} className="bg-coal border border-white/8 p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-xl text-gold">€{o.amount}</p>
            <p className="text-xs text-neutral-500 mt-1">→ {o.recipient_name} ({o.recipient_email})</p>
          </div>
          <div className="text-end">
            {o.code && <p className="text-sm text-gold font-mono tracking-widest">{o.code}</p>}
            <span className="text-[10px] uppercase tracking-widest text-neutral-400">{o.payment_status || o.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Dietary() {
  const { t } = useLang();
  const qc = useQueryClient();
  const { data: profile } = useQuery({ queryKey: ["food-profile"], queryFn: () => api.get("/profile/food").then((r) => r.data) });
  const [allergies, setAllergies] = useState(null);
  const [diets, setDiets] = useState(null);
  const a = allergies ?? (profile?.allergies || []).map((x) => (typeof x === "string" ? x : x.name)).join(", ");
  const d = diets ?? (profile?.dietary || []).join(", ");
  const save = async () => {
    try {
      await api.put("/profile/food", {
        allergies: a.split(",").map((s) => s.trim()).filter(Boolean).map((name) => ({ name, severity: "medium" })),
        dietary: d.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("✓");
      qc.invalidateQueries({ queryKey: ["food-profile"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="max-w-xl bg-coal border border-white/8 p-8 space-y-5" data-testid="dietary-panel">
      <div>
        <label className="lux-label">{t("account.allergies")}</label>
        <input data-testid="dietary-allergies-input" className="lux-input" placeholder={t("account.commaHint")} value={a} onChange={(e) => setAllergies(e.target.value)} />
      </div>
      <div>
        <label className="lux-label">{t("account.diets")}</label>
        <input data-testid="dietary-diets-input" className="lux-input" placeholder={t("account.commaHint")} value={d} onChange={(e) => setDiets(e.target.value)} />
      </div>
      <GoldButton onClick={save} data-testid="dietary-save-btn">{t("account.saveProfile")}</GoldButton>
    </div>
  );
}

function Addresses() {
  const { t } = useLang();
  const qc = useQueryClient();
  const { data: addresses } = useQuery({ queryKey: ["addresses"], queryFn: () => api.get("/addresses").then((r) => r.data) });
  const [form, setForm] = useState({ label: "", address: "", city: "" });
  const add = async () => {
    try {
      await api.post("/addresses", form);
      setForm({ label: "", address: "", city: "" });
      qc.invalidateQueries({ queryKey: ["addresses"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/addresses/${id}`);
      qc.invalidateQueries({ queryKey: ["addresses"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="addresses-panel">
      <div className="space-y-4">
        {(!addresses || addresses.length === 0) && <p className="text-neutral-500 text-sm">{t("common.empty")}</p>}
        {addresses?.map((ad) => (
          <div key={ad.id} className="bg-coal border border-white/8 p-5 flex items-center justify-between gap-4" data-testid={`address-item-${ad.id}`}>
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-1">{ad.label}</p>
              <p className="text-sm text-ivory">{ad.address}, {ad.city}</p>
            </div>
            <button data-testid={`address-delete-${ad.id}`} onClick={() => remove(ad.id)} className="text-neutral-600 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div className="bg-coal border border-white/8 p-8 space-y-4 h-fit">
        <input data-testid="address-label-input" className="lux-input" placeholder={t("account.label")} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        <input data-testid="address-street-input" className="lux-input" placeholder={t("booking.address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <input data-testid="address-city-input" className="lux-input" placeholder={t("booking.city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <OutlineButton onClick={add} disabled={!form.address || !form.city} data-testid="address-add-btn">{t("account.addAddress")}</OutlineButton>
      </div>
    </div>
  );
}
